import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ChevronRight, LayoutDashboard, Settings as SettingsIcon, ShoppingCart, Receipt, Grid2x2 } from "lucide-react";
import { getBackendBaseUrl, isNativePlatform } from "@/config/platform";
import { getUserPlan, hasPremiumAccess as canUsePremiumFeature } from "@/lib/saas";

// Pages
import LandingPage from "@/pages/LandingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ContactPage from "@/pages/ContactPage";
import Dashboard from "@/pages/Dashboard";
import Reservations from "@/pages/Reservations";
import Transactions from "@/pages/Transactions";
import Groceries from "@/pages/Groceries";
import Contracts from "@/pages/Contracts";
import FixedCosts from "@/pages/FixedCosts";
import LifePlanning from "@/pages/LifePlanning";
import HealthMoney from "@/pages/HealthMoney";
import Settings from "@/pages/Settings";
import AuthPage from "@/pages/AuthPage";
import BankCallback from "@/pages/BankCallback";
import OverviewPage from "@/pages/OverviewPage";
import ChurchPage from "@/pages/ChurchPage";
import ChurchRelations from "@/pages/ChurchRelations";
import ChurchDashboard from "@/pages/ChurchDashboard";
import ChurchReports from "@/pages/ChurchReports";

// Components
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = getBackendBaseUrl();
export const API = `${BACKEND_URL}/api`;
const Router = isNativePlatform() ? HashRouter : BrowserRouter;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("App render error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6">
          <div className="max-w-md rounded-2xl border border-border bg-white p-6 text-center shadow-card">
            <h1 className="font-heading text-2xl font-bold">Er ging iets mis</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Deze pagina kon niet goed worden geladen. Ververs de pagina of ga even terug naar het dashboard.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 inline-flex rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"
            >
              Pagina verversen
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const apiClient = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth interceptor
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = isNativePlatform() ? "/#/auth" : "/auth";
    }
    return Promise.reject(error);
  }
);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Invalid stored user session:", error);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await apiClient.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    toast.success(`Welkom terug, ${userData.name}!`);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await apiClient.post("/auth/register", { name, email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    toast.success(`Welkom bij budgettoppers, ${userData.name}!`);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.info("Je bent uitgelogd");
  };

  const upgradeToPremium = async () => {
    await apiClient.post("/premium/upgrade");
    const updatedUser = { ...user, is_premium: true };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);
    toast.success("Welkom bij budgettoppers Premium!");
  };

  const planType = getUserPlan(user);
  const hasPremiumAccess = (featureKey) => canUsePremiumFeature(featureKey, user);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, upgradeToPremium, planType, hasPremiumAccess }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-heading text-xl">Laden...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1024;
  });
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024;
  });

  const nativeApp = isNativePlatform();
  const useMobileShell = nativeApp && isCompactViewport;
  const mobilePrimaryNav = [
    { path: "/dashboard", label: "Dashboard", mobileLabel: "Home", icon: LayoutDashboard },
    { path: "/transactions", label: "Transacties", mobileLabel: "Geld", icon: Receipt },
    { path: "/overview", label: "Overzicht", mobileLabel: "Overzicht", icon: Grid2x2 },
    { path: "/groceries", label: "Boodschappen", mobileLabel: "Boodsch.", icon: ShoppingCart },
    { path: "/settings", label: "Instellingen", mobileLabel: "Instell.", icon: SettingsIcon },
  ];
  const routeTitles = {
    "/dashboard": "Dashboard",
    "/transactions": "Transacties",
    "/reservations": "Reserveringen",
    "/overview": "Overzicht",
    "/groceries": "Boodschappen",
    "/fixed-costs": "Vaste lasten",
    "/contracts": "Contracten",
    "/life-planning": "Levensplanning",
    "/health": "Gezondheid",
    "/settings": "Instellingen",
    "/church": "Kerkadministratie",
  };
  const currentTitle = routeTitles[location.pathname] || "BudgetToppers";

  useEffect(() => {
    const onResize = () => {
      setIsCompactViewport(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("native-shell-active", useMobileShell);
    document.body.classList.toggle("native-shell-active", useMobileShell);

    return () => {
      document.documentElement.classList.remove("native-shell-active");
      document.body.classList.remove("native-shell-active");
    };
  }, [useMobileShell]);

  return (
    <div className={`${useMobileShell ? "h-[100svh] min-h-0 flex-col" : "min-h-screen"} bg-background flex overflow-x-hidden`}>
      {!useMobileShell && sidebarOpen && (
        <button
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-label="Sluit menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {!useMobileShell && <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />}
      {useMobileShell && (
        <div className="z-30 border-b border-primary/30 bg-primary/95 text-primary-foreground shadow-[0_10px_24px_-18px_rgba(23,42,32,0.8)] backdrop-blur-xl">
          <div className="ios-safe-top-compact ios-safe-x flex min-h-[76px] items-center justify-between px-4 py-3">
            <div className="h-11 w-11 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1 px-4 text-center">
              <p className="truncate text-[15px] font-semibold text-primary-foreground">{currentTitle}</p>
            </div>
            <div className="h-11 w-11 shrink-0" aria-hidden="true" />
          </div>
        </div>
      )}
      <main
        className={`flex-1 min-h-0 min-w-0 overflow-x-hidden transition-all duration-300 ${useMobileShell ? "ios-scroll overflow-y-auto" : ""} ${!useMobileShell && sidebarOpen ? "lg:ml-64" : !useMobileShell ? "lg:ml-20" : ""}`}
      >
        {!useMobileShell && (
        <div className="sticky top-0 z-30 border-b border-white/60 bg-background/70 backdrop-blur-xl lg:hidden">
          <div className="ios-safe-top-compact ios-safe-x flex min-h-[76px] items-center justify-between px-4 py-3">
            {useMobileShell ? (
              <div className="h-11 w-11 shrink-0" aria-hidden="true" />
            ) : (
              <button
                onClick={() => setSidebarOpen(true)}
                className="liquid-glass-subtle inline-flex h-11 w-11 items-center justify-center rounded-2xl text-foreground"
                aria-label="Open menu"
              >
                <ChevronRight className="h-5 w-5 rotate-180" />
              </button>
            )}
            <div className="min-w-0 flex-1 px-4 text-center">
              <p className="truncate text-[15px] font-semibold text-foreground">{useMobileShell ? currentTitle : "BudgetToppers"}</p>
            </div>
            <div className="h-11 w-11 shrink-0" aria-hidden="true" />
          </div>
        </div>
        )}
        <div className={`ios-safe-x overflow-x-hidden p-4 sm:p-6 lg:p-8 ${useMobileShell ? "pt-4 pb-4" : "ios-safe-bottom pt-5"}`}>
          {children}
        </div>
      </main>
      {useMobileShell && (
        <nav className="ios-safe-x ios-safe-bottom z-30 border-t border-white/60 bg-background/80 px-3 pb-3 pt-2 backdrop-blur-xl">
          <div className="liquid-glass-surface mx-auto grid max-w-lg grid-cols-5 rounded-[28px] px-1.5 py-2">
            {mobilePrimaryNav.map((item) => {
              const active = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`flex min-h-[56px] flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[10px] font-medium leading-none transition-colors ${
                    active ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.mobileLabel || item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

function App() {
  const nativeRootElement = isNativePlatform() ? <Navigate to="/auth" replace /> : <LandingPage />;

  return (
    <Router>
      <AuthProvider>
        <AppErrorBoundary>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={nativeRootElement} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/voorwaarden" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/callback" element={<BankCallback />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    {localStorage.getItem('app_profile') === 'church' ? <ChurchDashboard /> : <Dashboard />}
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Transactions />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reservations"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Reservations />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/overview"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <OverviewPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groceries"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Groceries />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fixed-costs"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FixedCosts />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Contracts />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/life-planning"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <LifePlanning />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/health"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HealthMoney />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/church" element={<ProtectedRoute><AppLayout><ChurchPage /></AppLayout></ProtectedRoute>} />
            <Route path="/church-relations" element={<ProtectedRoute><AppLayout><ChurchRelations /></AppLayout></ProtectedRoute>} />
            <Route path="/church-reports" element={<ProtectedRoute><AppLayout><ChurchReports /></AppLayout></ProtectedRoute>} />
          </Routes>
        </AppErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;
